/**
 * @module ResourcePicker
 */

import {ClientApplication} from '../../client';
import {
  invalidActionType,
  invalidPayload,
  throwError,
  ActionType as ErrorActionType,
  ErrorAction,
} from '../Error';

import {actionWrapper, findMatchInEnum, getMergedProps, ActionSet} from '../helper';
import {ActionSetProps, Group, MetaAction} from '../types';

import {
  Action,
  ActionType,
  BaseOptions,
  ClosePayload,
  Options,
  Payload,
  ResourceSelection,
  ResourceType,
  SelectPayload,
} from './types';

export const GROUP = Group.ResourcePicker;

export interface ActionBase extends MetaAction {
  readonly group: typeof GROUP;
}

export interface SelectAction extends ActionBase {
  readonly type: typeof ActionType.SELECT;
  readonly payload: SelectPayload;
}

export interface UpdateAction extends ActionBase {
  readonly type: typeof ActionType.UPDATE;
  readonly payload: Payload;
}

export interface CloseAction extends ActionBase {
  readonly type: typeof ActionType.CLOSE;
}

export interface OpenAction extends ActionBase {
  readonly type: typeof ActionType.OPEN;
  readonly payload: Payload;
}

export type ResourcePickerAction =
  | SelectAction
  | UpdateAction
  | CloseAction
  | OpenAction
  | MetaAction;

export function select(payload: SelectPayload): SelectAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.SELECT,
  });
}

export function open(payload: Payload): OpenAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.OPEN,
  });
}

export function close(payload: ClosePayload): CloseAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.CLOSE,
  });
}

export function update(payload: Payload): UpdateAction {
  return actionWrapper({
    payload,
    group: GROUP,
    type: ActionType.UPDATE,
  });
}

function isValidResourceType(resourceType: string) {
  return findMatchInEnum(ResourceType, resourceType);
}

export function validationError(action: ResourcePickerAction): undefined | ErrorAction {
  let payload;
  switch (action.type) {
    case ActionType.UPDATE:
    case ActionType.OPEN:
      payload = (action as OpenAction).payload;

      return payload !== undefined &&
        payload.id !== undefined &&
        typeof payload.id === 'string' &&
        isValidResourceType(payload.resourceType)
        ? undefined
        : invalidPayload(action);

    case ActionType.SELECT:
      payload = (action as SelectAction).payload;

      return payload !== undefined &&
        payload.id !== undefined &&
        typeof payload.id === 'string' &&
        Array.isArray(payload.selection)
        ? undefined
        : invalidPayload(action);

    case ActionType.CLOSE:
      return undefined;

    default:
      return invalidActionType(action);
  }
}

export class ResourcePicker extends ActionSet implements ActionSetProps<Options, Payload> {
  readonly resourceType!: ResourceType;
  initialQuery?: string;
  title?: string;
  selectMultiple?: boolean;
  showHidden?: boolean;
  selection: ResourceSelection[] = [];

  constructor(app: ClientApplication<any>, options: Options, resourceType: ResourceType) {
    super(app, GROUP, GROUP);

    if (!isValidResourceType(resourceType)) {
      const errorAction = actionWrapper({
        group: GROUP,
        payload: {id: this.id, ...options, resourceType},
      });
      throwError(ErrorActionType.INVALID_OPTIONS, errorAction);
    }

    this.resourceType = resourceType;
    this.set(options, false);
  }

  get payload() {
    return {
      ...this.options,
      id: this.id,
      resourceType: this.resourceType,
    };
  }

  get options() {
    return {
      initialQuery: this.initialQuery,
      selectMultiple: this.selectMultiple,
      showHidden: this.showHidden,
      title: this.title,
    };
  }

  set(options: Partial<Options>, shouldUpdate = true) {
    const mergedOptions = getMergedProps(this.options, options);
    const {initialQuery, title, showHidden, selectMultiple} = mergedOptions;

    this.initialQuery = initialQuery;
    this.title = title;
    this.showHidden = showHidden === false || true;
    this.selectMultiple = !!selectMultiple;

    if (shouldUpdate) {
      this.update();
    }

    return this;
  }

  dispatch(action: Action, selection?: ResourceSelection[]) {
    if (action === Action.OPEN) {
      this.open();
    } else if (action === Action.UPDATE) {
      this.update();
    } else if (action === Action.CLOSE) {
      this.close();
    } else if (action === Action.SELECT) {
      this.selection = selection as ResourceSelection[];
      this.app.dispatch(select({id: this.id, selection: this.selection}));
    } else {
      throwError(
        ErrorActionType.INVALID_ACTION_TYPE,
        actionWrapper({
          group: this.group,
          type: action,
        }),
      );
    }

    return this;
  }

  protected update() {
    this.app.dispatch(update(this.payload));
  }

  protected open() {
    this.app.dispatch(open(this.payload));
  }

  protected close() {
    this.app.dispatch(close({id: this.id}));
  }
}

export const create = (app: ClientApplication<any>, baseOptions: BaseOptions) => {
  const {resourceType, options} = baseOptions;

  return new ResourcePicker(app, options, resourceType);
};



// WEBPACK FOOTER //
// ./src/actions/ResourcePicker/actions.ts